import { useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Card } from '@/components/ui/card';
import { toast } from 'sonner';
import { Phone, Mail, User, ArrowRight, ArrowLeft } from 'lucide-react';
import api from '@/utils/api';
import { setAuth } from '@/utils/auth';
import Header from '@/components/layout/Header';
import Footer from '@/components/layout/Footer';

const JobSeekerLogin = () => {
  const navigate = useNavigate();
  const [step, setStep] = useState('mobile'); // 'mobile', 'register', 'otp'
  const [mobile, setMobile] = useState('');
  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [otp, setOtp] = useState('');
  const [loading, setLoading] = useState(false);
  const [verificationId, setVerificationId] = useState('');

  // Helper to show error messages safely
  const showError = (error, fallback = 'An error occurred') => {
    const detail = error?.response?.data?.detail;
    if (Array.isArray(detail)) {
      toast.error(detail.map(e => e.msg || e.message || String(e)).join(', ') || fallback);
    } else if (typeof detail === 'object' && detail !== null) {
      toast.error(detail.msg || detail.message || fallback);
    } else if (typeof detail === 'string') {
      toast.error(detail);
    } else {
      toast.error(fallback);
    }
  };

  // Send OTP API call
  const sendOTP = async (mobileNumber) => {
    try {
      const response = await api.post('/auth/send-otp', { 
        mobile: mobileNumber, 
        role: 'job_seeker' 
      });
      
      console.log('Send OTP Response:', response.data);
      
      if (response.data.verificationId) {
        setVerificationId(response.data.verificationId);
        toast.success('OTP sent to your mobile number!');
        return true;
      } else if (response.data.message) {
        toast.success('OTP sent to your mobile number!');
        return true;
      }
      
      toast.error('Failed to send OTP');
      return false;
    } catch (error) {
      console.error('Send OTP Error:', error);
      showError(error, 'Failed to send OTP. Please try again.');
      return false;
    }
  };

  // Handle mobile number submission
  const handleMobileSubmit = async (e) => {
    e.preventDefault();
    if (mobile.length !== 10) {
      toast.error('Please enter a valid 10-digit mobile number');
      return;
    }
    
    setLoading(true);
    try {
      // First check if user exists WITHOUT sending OTP
      const checkResponse = await api.post('/auth/check-mobile', { 
        mobile, 
        role: 'job_seeker' 
      });
      
      console.log('Check Mobile Response:', checkResponse.data);
      
      if (checkResponse.data.exists) {
        // User exists - now send OTP
        const otpResponse = await api.post('/auth/send-otp', { 
          mobile, 
          role: 'job_seeker' 
        });
        
        if (otpResponse.data.verificationId) {
          setVerificationId(otpResponse.data.verificationId);
        }
        
        toast.success('OTP sent to your mobile number!');
        setStep('otp');
      } else {
        // New user - show registration form (NO OTP sent yet)
        toast.info('New user! Please complete registration.');
        setStep('register');
      }
    } catch (error) {
      console.error('Mobile Submit Error:', error);
      showError(error, 'Failed to check mobile number');
    } finally {
      setLoading(false);
    }
  };

  // Handle new user registration
  const handleRegister = async (e) => {
    e.preventDefault();
    if (!name.trim()) {
      toast.error('Please enter your name');
      return;
    }
    
    setLoading(true);
    try {
      // Register user
      const regResponse = await api.post('/auth/register', {
        mobile,
        name: name.trim(),
        email: email.trim() || undefined,
        role: 'job_seeker'
      });
      
      console.log('Register Response:', regResponse.data);
      toast.success('Registration successful!');
      
      // Send OTP for verification
      const otpSent = await sendOTP(mobile);
      if (otpSent) {
        setStep('otp');
      }
    } catch (error) {
      console.error('Register Error:', error);
      showError(error, 'Registration failed');
    } finally {
      setLoading(false);
    }
  };

  // Handle OTP resend
  const handleResendOTP = async () => {
    setLoading(true);
    const sent = await sendOTP(mobile);
    if (sent) {
      setOtp('');
    }
    setLoading(false);
  };

  // Handle OTP verification
  const handleOTPVerify = async (e) => {
    e.preventDefault();
    if (otp.length !== 6) {
      toast.error('Please enter a valid 6-digit OTP');
      return;
    }
    
    setLoading(true);
    try {
      console.log('Verifying OTP with:', { mobile, otp, verificationId });
      
      const response = await api.post('/auth/verify-otp', { 
        mobile, 
        otp, 
        role: 'job_seeker',
        verification_id: verificationId
      });
      
      console.log('Verify OTP Response:', response.data);
      
      setAuth(response.data.token, response.data.user);
      toast.success('Login successful!');
      navigate('/job-seeker/dashboard');
    } catch (error) {
      console.error('Verify OTP Error:', error);
      showError(error, 'Invalid OTP. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-slate-50">
      <Header />
      
      <main className="py-16">
        <div className="container mx-auto px-4">
          <div className="max-w-md mx-auto">
            <Card className="p-8 border-slate-200 shadow-lg">
              <div className="text-center mb-8">
                <h1 className="text-2xl font-bold text-slate-900" style={{fontFamily: 'Outfit'}}>
                  Job Seeker Login
                </h1>
                <p className="text-slate-600 mt-2">
                  {step === 'mobile' && 'Enter your mobile number to continue'}
                  {step === 'register' && 'Complete your registration'}
                  {step === 'otp' && 'Enter the OTP sent to your mobile'}
                </p>
              </div>

              {/* Mobile Number Step */}
              {step === 'mobile' && (
                <form onSubmit={handleMobileSubmit} className="space-y-4">
                  <div>
                    <label className="block text-sm font-medium text-slate-700 mb-2">
                      Mobile Number
                    </label>
                    <div className="relative">
                      <Phone className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-slate-400" />
                      <Input
                        type="tel"
                        placeholder="Enter 10-digit mobile number"
                        value={mobile}
                        onChange={(e) => setMobile(e.target.value.replace(/\D/g, '').slice(0, 10))}
                        className="pl-10"
                        required
                        maxLength={10}
                        data-testid="mobile-input"
                      />
                    </div>
                  </div>
                  <Button 
                    type="submit" 
                    className="w-full bg-teal-600 hover:bg-teal-700"
                    disabled={loading || mobile.length !== 10}
                    data-testid="continue-btn"
                  >
                    {loading ? 'Please wait...' : 'Continue'}
                    <ArrowRight className="w-4 h-4 ml-2" />
                  </Button>
                </form>
              )}

              {/* Registration Step */}
              {step === 'register' && (
                <form onSubmit={handleRegister} className="space-y-4">
                  <div>
                    <label className="block text-sm font-medium text-slate-700 mb-2">
                      Full Name <span className="text-red-500">*</span>
                    </label>
                    <div className="relative">
                      <User className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-slate-400" />
                      <Input
                        type="text"
                        placeholder="Enter your full name"
                        value={name}
                        onChange={(e) => setName(e.target.value)}
                        className="pl-10"
                        required
                        data-testid="name-input"
                      />
                    </div>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-slate-700 mb-2">
                      Email (Optional)
                    </label>
                    <div className="relative">
                      <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-slate-400" />
                      <Input
                        type="email"
                        placeholder="Enter your email"
                        value={email}
                        onChange={(e) => setEmail(e.target.value)}
                        className="pl-10"
                        data-testid="email-input"
                      />
                    </div>
                  </div>
                  <div className="flex gap-3">
                    <Button 
                      type="button" 
                      variant="outline"
                      onClick={() => setStep('mobile')}
                      className="flex-1"
                    >
                      <ArrowLeft className="w-4 h-4 mr-2" />
                      Back
                    </Button>
                    <Button 
                      type="submit" 
                      className="flex-1 bg-teal-600 hover:bg-teal-700"
                      disabled={loading || !name.trim()}
                      data-testid="register-btn"
                    >
                      {loading ? 'Registering...' : 'Register & Get OTP'}
                    </Button>
                  </div>
                </form>
              )}

              {/* OTP Verification Step */}
              {step === 'otp' && (
                <form onSubmit={handleOTPVerify} className="space-y-4">
                  <div>
                    <label className="block text-sm font-medium text-slate-700 mb-2">
                      Enter OTP
                    </label>
                    <Input
                      type="text"
                      placeholder="Enter 6-digit OTP"
                      value={otp}
                      onChange={(e) => setOtp(e.target.value.replace(/\D/g, '').slice(0, 6))}
                      className="text-center text-2xl tracking-widest"
                      maxLength={6}
                      required
                      data-testid="otp-input"
                    />
                    <p className="text-sm text-slate-500 mt-2">
                      OTP sent to {mobile}
                    </p>
                  </div>
                  <Button 
                    type="submit" 
                    className="w-full bg-teal-600 hover:bg-teal-700"
                    disabled={loading || otp.length !== 6}
                    data-testid="verify-btn"
                  >
                    {loading ? 'Verifying...' : 'Verify & Login'}
                  </Button>
                  <div className="flex justify-between items-center">
                    <Button 
                      type="button" 
                      variant="ghost"
                      onClick={() => {
                        setStep('mobile');
                        setOtp('');
                        setVerificationId('');
                      }}
                      className="text-slate-600"
                    >
                      <ArrowLeft className="w-4 h-4 mr-1" />
                      Change Number
                    </Button>
                    <Button 
                      type="button" 
                      variant="link"
                      onClick={handleResendOTP}
                      disabled={loading}
                      className="text-teal-600"
                      data-testid="resend-btn"
                    >
                      Resend OTP
                    </Button>
                  </div>
                </form>
              )}

              <div className="mt-6 text-center text-sm text-slate-600">
                <p>
                  Are you an employer?{' '}
                  <Link to="/employer/login" className="text-teal-600 hover:underline font-medium">
                    Employer Login
                  </Link>
                </p>
              </div>
            </Card>
          </div>
        </div>
      </main>

      <Footer />
    </div>
  );
};

export default JobSeekerLogin;
